/*
 * Baddie V2.3 behaviour.
 * - [data-bd-takeover]  colour-takeover hero: circular colour reveal from the pressed tab,
 *                       autoplay that pauses on hover, focus, off-screen and hidden tabs.
 * - [data-bd-quiz]      Find-your-Baddie: three taps to a product + pack recommendation.
 * - [data-bd-explorer]  ingredient hotspots over a photo.
 * - [data-bd-celebrate] one-time burst when the cart unlocks free delivery.
 * Everything renders server-side first; this file only adds interaction.
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ---------- Colour takeover hero ---------- */

const START_EVENTS = ['scroll', 'pointerdown', 'keydown', 'touchstart'];

class Takeover {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.stage = root.querySelector('.bd-takeover__stage');
    this.slides = [...root.querySelectorAll('.bd-takeover__slide')];
    this.tabs = [...root.querySelectorAll('.bd-takeover__tab')];
    this.pauseButton = root.querySelector('[data-bd-takeover-pause]');
    this.index = 0;
    this.interval = Number(root.dataset.interval) || 7000;
    this.autoplay = root.dataset.autoplay === 'true' && !reducedMotion.matches && this.slides.length > 1;
    this.userPaused = false;
    this.hovered = false;
    this.visible = true;
    this.timer = 0;
    // Autoplay starts on the first interaction, so a later (larger) slide never becomes the page's LCP.
    this.ready = false;

    root.style.setProperty('--bd-interval', `${this.interval}ms`);

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        this.go(index, tab);
        this.restart();
      });
      tab.addEventListener('keydown', (event) => this.onKey(event, index));
    });

    this.pauseButton?.addEventListener('click', () => {
      this.userPaused = !this.userPaused;
      this.pauseButton.setAttribute('aria-pressed', String(this.userPaused));
      this.restart();
    });

    root.addEventListener('pointerenter', () => {
      this.hovered = true;
      this.restart();
    });
    root.addEventListener('pointerleave', () => {
      this.hovered = false;
      this.restart();
    });
    root.addEventListener('focusin', () => {
      this.hovered = true;
      this.restart();
    });
    root.addEventListener('focusout', (event) => {
      if (!root.contains(/** @type {Node} */ (event.relatedTarget))) {
        this.hovered = false;
        this.restart();
      }
    });
    document.addEventListener('visibilitychange', () => this.restart());

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.restart();
      }).observe(root);
    }

    const start = () => {
      if (this.ready) return;
      this.ready = true;
      START_EVENTS.forEach((type) => window.removeEventListener(type, start));
      this.slides.forEach((slide) => slide.querySelectorAll('img[loading=lazy]').forEach((img) => (img.loading = 'eager')));
      this.restart();
    };
    START_EVENTS.forEach((type) => window.addEventListener(type, start, { passive: true }));

    this.restart();
  }

  get playing() {
    return this.ready && this.autoplay && !this.userPaused && !this.hovered && this.visible && !document.hidden;
  }

  restart() {
    clearTimeout(this.timer);
    this.root.classList.toggle('is-playing', this.playing);
    if (!this.playing) return;
    // Restart the progress animation on the active tab.
    const active = this.tabs[this.index]?.querySelector('.bd-takeover__progress');
    if (active) {
      active.style.animation = 'none';
      void active.offsetWidth;
      active.style.animation = '';
    }
    this.timer = window.setTimeout(() => {
      const next = (this.index + 1) % this.slides.length;
      this.go(next, this.tabs[next]);
      this.restart();
    }, this.interval);
  }

  /** @param {number} next @param {HTMLElement | undefined} origin */
  go(next, origin) {
    if (next === this.index) return;
    const current = this.slides[this.index];
    const incoming = this.slides[next];

    if (origin && this.stage) {
      const stageBox = this.stage.getBoundingClientRect();
      const tabBox = origin.getBoundingClientRect();
      const x = ((tabBox.left + tabBox.width / 2 - stageBox.left) / stageBox.width) * 100;
      incoming.style.setProperty('--bd-x', `${Math.max(0, Math.min(100, x))}%`);
      incoming.style.setProperty('--bd-y', '100%');
    }

    incoming.removeAttribute('inert');
    incoming.setAttribute('aria-hidden', 'false');
    incoming.classList.add('is-active');
    incoming.querySelectorAll('a').forEach((link) => link.removeAttribute('tabindex'));

    const finish = () => {
      incoming.classList.remove('is-entering');
      current.classList.remove('is-active');
      current.setAttribute('aria-hidden', 'true');
      current.setAttribute('inert', '');
      current.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
    };

    if (reducedMotion.matches) {
      finish();
    } else {
      incoming.classList.add('is-entering');
      incoming.addEventListener('animationend', finish, { once: true });
    }

    this.tabs.forEach((tab, index) => tab.setAttribute('aria-selected', String(index === next)));
    this.index = next;
  }

  /** @param {KeyboardEvent} event @param {number} index */
  onKey(event, index) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const rtl = document.documentElement.dir === 'rtl';
    let next = index;
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.tabs.length - 1;
    else next = index + ((event.key === 'ArrowRight') !== rtl ? 1 : -1);
    next = (next + this.tabs.length) % this.tabs.length;
    this.tabs[next].focus();
    this.go(next, this.tabs[next]);
    this.restart();
    event.preventDefault();
  }
}

const initTakeovers = (root = document) => {
  root.querySelectorAll('[data-bd-takeover]').forEach((element) => {
    if (!element.bdTakeover) element.bdTakeover = new Takeover(/** @type {HTMLElement} */ (element));
  });
};

initTakeovers();
document.addEventListener('shopify:section:load', (event) => initTakeovers(/** @type {Element} */ (event.target)));

/* ---------- Find your Baddie quiz ---------- */

const pickResult = ({ who, when }) => {
  if (who === 'couple') return when === 'period' ? 'all' : 'couples';
  if (who === 'man') return 'men';
  return when === 'period' ? 'period' : 'women';
};

const initQuiz = (root) => {
  if (root.bdQuiz) return;
  root.bdQuiz = true;
  const questions = [...root.querySelectorAll('.bd-quiz__q')];
  const steps = [...root.querySelectorAll('.bd-quiz__steps li')];
  const results = [...root.querySelectorAll('.bd-quiz__result')];
  const restart = root.querySelector('[data-quiz-restart]');
  const answers = {};

  const show = (index) => {
    questions.forEach((question, i) => {
      question.hidden = i !== index;
      question.classList.toggle('is-current', i === index);
    });
    steps.forEach((step, i) => {
      step.classList.toggle('is-current', i === index);
      step.classList.toggle('is-done', i < index);
    });
  };

  const showResult = () => {
    const key = pickResult(answers);
    questions.forEach((question) => (question.hidden = true));
    steps.forEach((step) => {
      step.classList.remove('is-current');
      step.classList.add('is-done');
    });
    results.forEach((result) => {
      const match = result.dataset.result === key;
      result.hidden = !match;
      if (!match) return;
      const ids = (result.dataset.variants || '').split(',');
      const prices = (result.dataset.prices || '').split('|');
      const titles = (result.dataset.titles || '').split('|');
      const pick = Math.min(Math.max(Number(answers.often) || 1, 1), ids.length) - 1;
      const input = result.querySelector('[data-quiz-variant]');
      if (input && ids[pick]) input.value = ids[pick];
      const pack = result.querySelector('[data-quiz-pack]');
      if (pack && titles[pick]) pack.textContent = titles[pick];
      const price = result.querySelector('[data-quiz-price]');
      if (price && prices[pick]) price.textContent = prices[pick];
      result.classList.add('is-in');
      const title = result.querySelector('.bd-quiz__result-title');
      title?.setAttribute('tabindex', '-1');
      title?.focus({ preventScroll: true });
    });
    if (restart) restart.hidden = false;
  };

  root.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const option = target?.closest('.bd-quiz__opt');
    if (option) {
      const question = option.closest('.bd-quiz__q');
      const index = questions.indexOf(question);
      answers[question.dataset.q] = option.dataset.value;
      question.querySelectorAll('.bd-quiz__opt').forEach((other) => other.setAttribute('aria-pressed', String(other === option)));
      if (index < questions.length - 1) {
        show(index + 1);
        questions[index + 1].querySelector('.bd-quiz__opt')?.focus({ preventScroll: true });
      } else {
        showResult();
      }
      return;
    }

    if (target?.closest('[data-quiz-restart]')) {
      results.forEach((result) => {
        result.hidden = true;
        result.classList.remove('is-in');
      });
      questions.forEach((question) => question.querySelectorAll('.bd-quiz__opt').forEach((other) => other.removeAttribute('aria-pressed')));
      if (restart) restart.hidden = true;
      show(0);
      questions[0].querySelector('.bd-quiz__opt')?.focus({ preventScroll: true });
    }
  });
};

document.querySelectorAll('[data-bd-quiz]').forEach(initQuiz);
document.addEventListener('shopify:section:load', (event) =>
  /** @type {Element} */ (event.target).querySelectorAll('[data-bd-quiz]').forEach(initQuiz)
);

/* ---------- Ingredient explorer ---------- */

const initExplorer = (root) => {
  if (root.bdExplorer) return;
  root.bdExplorer = true;
  const spots = [...root.querySelectorAll('.bd-explorer__spot')];
  const items = [...root.querySelectorAll('.bd-explorer__item')];

  const select = (index) => {
    spots.forEach((spot, i) => spot.setAttribute('aria-expanded', String(i === index)));
    items.forEach((item, i) => item.classList.toggle('is-active', i === index));
  };

  spots.forEach((spot, index) => {
    spot.addEventListener('click', () => {
      const open = spot.getAttribute('aria-expanded') === 'true';
      select(open ? -1 : index);
      if (!open && window.matchMedia('(max-width: 989px)').matches) {
        items[index]?.scrollIntoView({ block: 'nearest', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }
    });
    spot.addEventListener('pointerenter', () => {
      if (window.matchMedia('(hover: hover)').matches) select(index);
    });
  });

  items.forEach((item, index) => {
    item.addEventListener('pointerenter', () => select(index));
  });

  root.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') select(-1);
  });

  select(0);
};

document.querySelectorAll('[data-bd-explorer]').forEach(initExplorer);
document.addEventListener('shopify:section:load', (event) =>
  /** @type {Element} */ (event.target).querySelectorAll('[data-bd-explorer]').forEach(initExplorer)
);

/* ---------- Landing header cart button ---------- */

document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('[data-bd-open-cart]') : null;
  if (!trigger) return;
  const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
  if (drawer && typeof drawer.open === 'function') drawer.open();
  else window.location.href = '/cart';
});

const refreshCartCount = async () => {
  const counters = document.querySelectorAll('[data-bd-cart-count]');
  if (!counters.length) return;
  try {
    const cart = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`).then((res) => res.json());
    counters.forEach((counter) => (counter.textContent = String(cart.item_count)));
  } catch (error) {
    /* the count stays as rendered */
  }
};

// Horizon and bd.js both announce cart changes with the standard cart:lines-update event.
document.addEventListener('shopify:cart:lines-update', (event) => {
  const promise = /** @type {any} */ (event).promise;
  if (promise && typeof promise.then === 'function') promise.then(refreshCartCount, refreshCartCount);
  else setTimeout(refreshCartCount, 1200);
});

/* ---------- Free-delivery unlock celebration ---------- */

const CELEBRATED_KEY = 'bd-free-delivery-celebrated';

const readCelebrated = () => {
  try {
    return sessionStorage.getItem(CELEBRATED_KEY) === '1';
  } catch (error) {
    return false;
  }
};

const markCelebrated = () => {
  try {
    sessionStorage.setItem(CELEBRATED_KEY, '1');
  } catch (error) {
    /* storage unavailable: celebrate again next time, harmless */
  }
};

let shipWasDone = !!document.querySelector('.bd-ship--done');

const watchShipping = () => {
  const done = document.querySelector('.bd-ship--done');
  if (done && !shipWasDone && !readCelebrated() && !reducedMotion.matches) {
    done.classList.add('is-celebrating');
    markCelebrated();
  }
  shipWasDone = !!done;
};

// Only the cart drawer and the cart page can change the free-delivery bar; checks are batched per frame.
let shipCheckQueued = false;
const shipObserver = new MutationObserver(() => {
  if (shipCheckQueued) return;
  shipCheckQueued = true;
  requestAnimationFrame(() => {
    shipCheckQueued = false;
    watchShipping();
  });
});
document.querySelectorAll('#cart-drawer, .cart-page__items').forEach((target) =>
  shipObserver.observe(target, { childList: true, subtree: true })
);
